<template>
  <div
    :id="`${id}-dropdown`"
    ref="mainRef"
    v-click-outside="handleBlur"
    :data-testid="dataTestid"
    class="relative"
    :class="[
      wrapperClass,
      component.wrapper,
      {
        'flex flex-wrap items-center gap-2 border-1 border-semantic-outline-default rounded-lg min-h-12 py-2 px-4 cursor-text pr-12': multiple,
      }
    ]"
    @keydown.up="$event => (handleActions($event, 'up'))"
    @keydown.down="$event => (handleActions($event, 'down'))" 
    @keydown.enter="$event => (handleActions($event, 'enter'))"
    @keydown.tab="$event => (handleActions($event, 'tab'))"
    @keydown.backspace="$event => (handleActions($event, 'backspace'))"
    @click="handleFocus"
  >
    <div
      v-if="
        (!!errorMessage || ['active', 'typing', 'error'].includes(inputStatus)) && inputLabel && multiple
      "
      ref="label"
      class="absolute left-0 top-[10px] pl-4 text-xs leading-[1.125rem] text-semantic-primary-on-default z-[1] w-full"
    >
      <p
        class="absolute bottom-0 px-1 mr-3"
        :class="inputStyles.label"
      >
        {{ inputLabel }}
      </p>
    </div>

    <ul
      v-if="multiple && !_isEmpty(_selected?.list)"
      ref="selectedArray"
      class="w-auto h-full flex flex-wrap items-center gap-2 text-semantic-surface-on-surface pointer-events-none"
      @click.stop
    >
      <li
        v-for="(item, itemKey) in _selected.list"
        :key="itemKey"
        class="py-1 px-2 bg-semantic-surface-container-low border-1 border-semantic-primary-default rounded-2md flex gap-1 justify-center items-center pointer-events-none text-base-font-size-base"
        @click.stop
      >
        <span>
          {{ item?.[props.dataInputDisplay] ?? item }}
        </span>

        <V2IconsCancelCircle
          width="16"
          height="16"
          :fill="semanticColorLookup('primary.default')"
          class="cursor-pointer pointer-events-auto"
          @click.stop="removeSelection(item)"
        />
      </li>
    </ul>

    <V2AtomsInput
      ref="inputRef"
      v-model="inputText"
      v-bind="{ ...$attrs, autocomplete: $attrs.autocomplete || 'off' }"
      type="text"
      :placeholder="defaultValue || $attrs.placeholder || label"
      :mobile-keyboard-type="mobileKeyboardType"
      :label="label"
      :model-type="modelType"
      :searchable="searchable"
      :readonly="$attrs.readonly || !searchable"
      :input-area-class="selectionClass"
      :chevron-config="chevronConfig"
      :error-message="errorMessage"
      :is-success="isSuccess"
      :hide-icon="hideDropdownIcon || multiple"
      :no-style="multiple"
      :wrapper-class="{
        'flex-auto': multiple,
        'h-inherit': !multiple,
        'w-1/2': multiple && searchable
      }"
      :class="{
        '!font-bold': !_isEmpty(inputText) && selectedDisplayBold,
        'h-inherit pr-12': !multiple,
      }"
      @focus="handleFocus"
      @input="() => handleFilterListDebounced()"
      @keydown="handleKeydownFilter"
    />

    <V2IconsCaret
      v-if="multiple"
      ref="arrow"
      class="absolute-y-center right-4"
      :direction="['focused', 'active', 'typing'].includes(inputStatus) ? 'top' : 'bottom'"
      :class="{
        'fill-semantic-primary-default': ['active', 'typing'].includes(inputStatus),
        'fill-semantic-surface-on-surface-variant': !['active', 'typing'].includes(inputStatus),
        '!fill-semantic-states-error-default': inputStatus === 'error'
      }"
      fill=""
    />

    <Teleport to="body">
      <div
        v-show="toggle"
        :id="`${id}-dropdown-list`"
        ref="listRef"
        :style="{
          left: listRect.left,
          top: listRect.top,
          width: listRect.width
        }"
        class="dropdown-list absolute w-full z-[999] pb-2 shadow-sm-3 rounded-[0.625rem] overflow-hidden bg-white"
        :class="[
          {
            '!p-0': _isEmpty(filteredList)
          },
          dropdownClass
        ]"
        @click.stop
        @keydown.tab="$event => (handleActions($event, 'tab'))"
      >
        <div
          v-if="multiple && showSelectAll"
          class="flex justify-between px-4 mb-2 bg-semantic-surface-container-low font-bold text-xs"
        >
          <span
            class="py-2 cursor-pointer"
            @click="handleSelectAll"
          >
            Select all
          </span>

          <span
            class="py-2 cursor-pointer"
            @click="handleClearAll"
          >
            Clear
          </span>
        </div>

        <ul
          v-if="!_isEmpty(filteredList)"
          ref="dropdownListRef"
          class="overflow-auto max-h-[356px]"
        >
          <li
            v-for="(item, itemKey) in filteredList"
            :key="itemKey"
            ref="dropdownListItemsRef"
            class="cursor-pointer flex flex-grow text-semantic-surface-on-surface relative"
            :class="{
              '!bg-semantic-surface-container-low': (itemKey === flags.mainFocus) && !item?.options,
              'bg-blue-5': checkSelected(item) && !item?.options,
              'items-center px-4 py-2 hover:bg-semantic-surface-container-low': !item?.options,
              'flex-col [&>ul]:not-last:border-b-1 [&>ul]:not-last:border-semantic-outline-variant': item?.options,
            }"
            v-on="{
              ...((!item?.options && {
                click: () => handleSelectItem(item)
              }) || {})
            }"
          >
            <span
              v-if="!item?.options"
            >
              {{ handleDisplayStr((item && item[dataListDisplay]) || item) }}
            </span>

            <template v-else>
              <span class="font-bold p-4 pb-2 cursor-default">
                {{ item?.label }}
              </span>

              <ul ref="dropdownSubListRef">
                <li
                  v-for="(opt, optIndex) in item?.options"
                  ref="dropdownSubItemsRef"
                  :key="optIndex"
                  class="px-4 py-2"
                  :class="{
                    'bg-semantic-surface-container-low': (optIndex === flags.subFocus) && (itemKey === flags.mainFocus),
                    'bg-blue-5': checkSelected(opt),
                  }"
                  @click="handleSelectItem(opt)"
                >
                  {{ opt?.text }}
                </li>
              </ul>
            </template>
          </li>
        </ul>
      </div>
    </Teleport>
  </div>
</template>

<script setup>
import { storeToRefs } from 'pinia'

import _flatten from 'underscore/cjs/flatten.js'
import _isEmpty from 'underscore/cjs/isEmpty.js'
import _size from 'underscore/cjs/size.js'
import _debounce from 'underscore/cjs/debounce.js'

defineOptions({
  name: 'V2AtomsDropdown',
  inheritAttrs: false
})

const props = defineProps({
  dataTestid: {
    type: String,
    default: ''
  },

  modelValue: {
    type: [String, Array, Object],
    default: ''
  },

  modelType: {
    type: String,
    default: 'dropdown',
    validator: value => ['text', 'dropdown'].includes(value)
  },

  multiple: {
    type: Boolean,
    default: false
  },

  searchable: {
    type: Boolean,
    default: false
  },

  hideDropdownIcon: {
    type: Boolean,
    default: false
  },

  label: {
    type: String,
    default: 'Label'
  },

  errorMessage: {
    type: String,
    default: ''
  },

  isSuccess: {
    type: Boolean,
    default: false
  },

  dataArr: {
    type: Array,
    default: () => []
  },

  // show dropdown list if character length is equal to characterDelay
  characterDelay: {
    type: [String, Number],
    default: 0,
    validator: value => !isNaN(value)
  },

  /**
   * limit list to be displayed
   */
  dataListLimit: {
    type: [String, Number],
    default: 0,
    validator: value => !isNaN(value)
  },

  // url to fetch array
  dataSrc: {
    type: String,
    default: ''
  },

  // key to access list of array on fetch
  dataSrcKey: {
    type: String,
    default: 'data'
  },

  // key used to display data in dropdown
  dataListDisplay: {
    type: [String, Number],
    default: 'name'
  },

  // key used to display data in input
  dataInputDisplay: {
    type: [String, Number],
    default: 'name'
  },

  /**
   * key used for emitting data to parent component
   * if value is empty it return the whole selected item
   */
  dataValue: {
    type: [String, Number],
    default: ''
  },

  customizeDataDisplay: {
    type: Function,
    default: null
  },

  /**
   * prop for overriding filter method and emiting
   * the value for external search filter
   */
  externalFilter: {
    type: Boolean,
    default: false
  },

  selectionClass: {
    type: String,
    default: ''
  },

  chevronConfig: {
    type: Object,
    default: () => ({})
  },

  selectedDisplayBold: {
    type: Boolean,
    default: false
  },

  dropdownClass: {
    type: String,
    default: ''
  },

  // height of the wrapper or the whole component
  wrapperClass: {
    type: String,
    default: ''
  },

  defaultValue: {
    type: [String, Number, Array, Object],
    default: ''
  },

  mobileKeyboardType: {
    type: String,
    default: 'text'
  },

  /**
   * Used together with multiple prop.
   * This shows or hides the select all / clear section
   */
  showSelectAll: {
    type: Boolean,
    default: false
  },

  dataTestid: {
    type: String,
    default: 'v2-atoms-dropdown'
  }
})

const emit = defineEmits([
  'update:modelValue',
  'filter',
  'input',
  'api-options',
  'search-result'
])

const $attrs = useAttrs()
const { devices } = deviceChecker()

const layoutsStore = useLayoutsStore()
const { scrollPosition } = storeToRefs(layoutsStore)

const flags = reactive({
  focus: false,
  isTyping: false,
  isHandlingClick: false,

  mainFocus: -1,
  subFocus: -1
})

const mainRef = ref()
const inputRef = ref()
const dropdownListRef = ref()
const dropdownListItemsRef = ref()
const dropdownSubListRef = ref()
const dropdownSubItemsRef = ref()

const listRef = ref()
const listRect = reactive({
  width: 0,
  top: 0,
  left: 0
})

const toggle = ref(false)

const initialSelection = ref()
const inputText = ref()
const selected = reactive({
  values: [],
  input: []
})

const filteredList = ref()
const id = computed(() => useId())

const delay = (!props.characterDelay || isNaN(props.characterDelay)) ? 0 : +props.characterDelay

/**
 * returns the selected list
 * if there is an initial value
 */
const _selected = computed(() => {
  const tempList = flatGroupList.value.length
    ? flatGroupList.value
    : list.value
  const tempSelected = []

  for (let i = 0; i < tempList.length; i++) {
    const item = tempList[i]

    if (
      !(checkSelected(item) &&
      tempSelected.findIndex(e => JSON.stringify(e) === JSON.stringify(item)) === -1)
    ) {
      continue
    }

    tempSelected.push(item)
  }

  if (_isEmpty(tempSelected)) {
    return {
      input: [],
      values: []
    }
  }

  return {
    input: tempSelected.map(item => item?.[props.dataInputDisplay] ?? item),
    values: tempSelected.map(item => item?.[props.dataValue] ?? item),
    list: tempSelected,
    inputText: (() => {
      /**
       * if `props.multple` true
       * `inputText.value` is set to empty string
       *  instead `inputSelected` is displayed
       */
      if (props.multiple) {
        return ''
      }

      return tempSelected[0]?.[props.dataInputDisplay] ?? tempSelected[0]
    })()
  }
})

const inputSelected = computed(() => {
  if (_isEmpty(_selected.value.input) || !props.multiple) {
    return ''
  }

  const map = _selected.value.input.map(item => item[props.dataInputDisplay] ?? item)

  return map.join(', ')
})

const inputLabel = computed(() => inputRef.value?.fieldLabel)
const inputStatus = computed(() => {
  const _status = inputRef.value?.status ?? 'default'

  if (flags.isTyping) {
    return 'typing'
  }

  return _status
})
const inputStyles = computed(() => inputRef.value?.component)

const component = computed(() => {
  const wrapper = (() => { 
    const temp = {
      focused: 'ring-4 !border-0',
      default: _isEmpty(currValue.value)
        ? ''
        : '!border-semantic-outline-default'
    }

    return `${inputStyles.value?.statusStyles} ${temp?.[inputStatus.value]}`
  })()

  return {
    wrapper
  }
})

const dataList = ref([])

const listener = ref()
listener.value = new AbortController()

const signal = ref(listener.value.signal)

onMounted(async () => {
  await nextTick()
  calculatePosition()

  /* v8 ignore start */
  window.addEventListener('blur', () => { 
    if (toggle.value) {
      handleBlur()
    }
  }, { signal: signal.value })
  /* v8 ignore end */
  
  /**
   * move dataList handling to onMounted
   * to prevent undefined parentNode ssr bug
   */
  dataList.value = _isEmpty(props.dataSrc)
    ? []
    : await useFetch(props.dataSrc)
      .then(res => {
        const temp = res?.data?.value?.[props.dataSrcKey] ?? res?.data?.value ?? []

        emit('api-options', temp)

        if (!(isObject(temp) || isArray(temp))) {
          return []
        }

        return temp
      })

  initializeData()

  if (!_isEmpty(selected.values)) {
    const value = props.multiple
      ? selected.values
      : selected.values[0]

    emit('update:modelValue', value)
  }

  /**
   * set initial selection
   */
  if (_isEmpty(initialSelection.value)) {
    initialSelection.value = JSON.parse(JSON.stringify(selected))
  }
})

onUnmounted(() => {
  listener.value.abort()
})

/**
 * List of options
 * returns `dataList` if dataSrc is not empty
 * otherwise it returns `props.dataArr`
 */
const list = computed(() => (JSON.parse(JSON.stringify(props.dataSrc ? dataList.value : props.dataArr))))

/**
 * checks if `LIST` array has `options` key
 * to identify if its going to be displayed
 * by group
 */
const isGrouped = computed(() => { 
  const _opt = list?.value?.[0]?.options

  return Array.isArray(_opt) && typeof _opt === 'object' && [...new Set(list.value.flatMap(obj => Object.keys(obj)))].includes('options')
})

/**
 * returns the flattened `LIST.value` 
 * if its displayed by group
 */
const flatGroupList = computed(() => { 
  if (!isGrouped.value) {
    return []
  }

  const temp = _flatten(list.value.map(e => e.options))

  return Array.from([...new Map(temp.map(item => [item?.[props.dataListDisplay], item])).values()])
})

const optionsSizeByGroup = computed(() => { 
  const TEMP = list.value.map(e => e?.options ? _size(e?.options) : null)

  return TEMP.every(e => [null, undefined].includes(e))
    ? null
    : TEMP
})

const currValue = computed(() => {
  const temp = Array.isArray(selected.values)
    ? selected.values
    : [selected.values]

  const tempModelValue = Array.isArray(props.modelValue)
    ? props.modelValue
    : [props.modelValue]

  return !_isEmpty(temp) ? temp : tempModelValue
})

watch(() => props.modelValue, val => {
  if (isNullish(val) || val === '') {
    inputText.value = ''
    selected.values = []
    selected.input = []
  } else {
    initializeData()
  }
}, { deep: true })

watch(inputText, val => {
  /**
   * emits `inputText.value` if its empty
   */
  if (
    (isNullish(val) || val === '') &&
    !props.multiple
  ) {
    selected.values = []
    selected.input = []
    emit('update:modelValue', inputText.value)
  }
}, { deep: true })

watch(() => props.dataArr, val => {
  if (props.externalFilter) {
    filteredList.value = val
  }
}, { deep: true })

watch(() => devices.width.value, () => calculatePosition())
watch(scrollPosition, () => calculatePosition())

/**
 * initializes data if there is an existing data
 */
function initializeData () {
  if (!(
    isNullish(props.modelValue) ||
    props.modelValue === '' 
  )) {
    selected.values = _selected.value.values
    selected.input = _selected.value.input

    if (!props.multiple) {
      inputText.value = _selected.value.inputText
    }
  }
}

/**
 * Filters list of options upon input or focus
 */
async function handleFilterList () {
  flags.isTyping = !_isEmpty(inputText.value)

  if (props.searchable) {
    handleShowDropdown()
  }

  if (props.externalFilter) {
    emit('filter', inputText.value)
    return
  }

  const TEMP_LIST = (() => {
    /**
     * if display is by group
     * return flattened list if user
     * is typing
     */    
    if (flags.isTyping && isGrouped.value) {
      return flatGroupList.value
    }

    return list.value
  })()

  /**
   * returns the original list of options if
   * its not `searchable`
   */
  if (!props.searchable) {
    filteredList.value = TEMP_LIST
    return
  }

  if (!(isObject(TEMP_LIST) || isArray(TEMP_LIST))) {
    return
  }

  const text = (inputText.value && inputText.value.toString().toLowerCase()) || ''

  filteredList.value = TEMP_LIST.filter(item => {
    return `${item?.[props.dataListDisplay] ?? item}`
      .toLowerCase()
      .includes(text)
  })

  const matches = filteredList.value.length

  /**
   * if `props.dataListLimit` is not empty
   * it returns a limited number of list options
   */
  if (+props.dataListLimit) {
    filteredList.value = filteredList.value.slice(0, +props.dataListLimit)
  }

  emit('search-result', { text: inputText.value, matches })

  await wait(10)
  flags.mainFocus = -1
  flags.subFocus = -1

  calculatePosition()
}

// reset list structure 
function handleKeydownFilter () {
  if (flags.isTyping) { 
    return
  }

  if (flags.isTyping && isGrouped.value) {
    filteredList.value = flatGroupList.value
  }

  filteredList.value = list.value
}

const handleFilterListDebounced = _debounce(handleFilterList, 400)

function handleSelectItem (item) {
  try {
    const inputDisplay = JSON.parse(JSON.stringify(item))?.[props?.dataInputDisplay] ?? item
    const inputValue = JSON.parse(JSON.stringify(item))?.[props.dataValue] ?? item

    /**
     * empties the inputText value if
     * `props.searchable` and `props.multiple` is true
     * it shows the `selected-multiple-items` ref
     * instead with ellipsis
     */
    if (props.searchable && props.multiple) {
      inputText.value = ''
    }

    if (!props.multiple) {
      selected.values = inputValue
      selected.input = inputDisplay

      inputText.value = inputDisplay
    } else {
      /**
       * checkes if item is selected.
       * if its selected it adds it to the array
       * otherwise, it removes it in the list
       */
      const selectedIndex = selected.values.findIndex(key => JSON.stringify(key) === JSON.stringify(inputValue))

      if (selectedIndex === -1) {
        selected.values.push(inputValue)
        selected.input.push(inputDisplay)
      } else {
        selected.values.splice(selectedIndex, 1)
        selected.input.splice(selectedIndex, 1)
      }
    }

    emit('update:modelValue', selected.values)
    emit('input', selected.values)
    handleBlur()
  } catch (error) {
    throw new Error(error?.message ?? String(error), { cause: error })
  }
}

/**
 * if has `enableSpecialCharacterDelay` it checks the list after keyword show
 */
function shouldShowDropdown () {
  const specialCharacter = props.enableSpecialCharacterDelay

  if (specialCharacter) {
    return inputText.value?.includes(specialCharacter)
  }

  return inputSelected.value || _size(inputText.value) >= delay
}

/**
 * if has `characterDelay` it checks if the keyword length is
 * greater than or equal to delay number before it shows the list
 */
function handleShowDropdown () {
  if (shouldShowDropdown()) {
    toggle.value = true
    calculatePosition()
  }
}

function handleSelectAll () {
  selected.input = list.value.map(item => item?.[props.dataInputDisplay] ?? item)
  selected.values = list.value.map(item => item?.[props.dataValue] ?? item)

  emit('update:modelValue', selected.values)

  handleBlur()
}

function handleClearAll () {
  inputText.value = ''
  selected.input = []
  selected.values = []

  emit('update:modelValue', selected.values)

  handleBlur()
}

/**
 * returns customized string to display
 * in options list
 */
function handleDisplayStr (str) {
  if (props.customizeDataDisplay) {
    return props?.customizeDataDisplay(str)
  }

  if (Array.isArray(str)) {
    return str.join(', ')
  }

  return str
}

/**
 * checks if options is selected or not
 * returns Boolean
 */
function checkSelected (option) {
  if (
    _isEmpty(option) ||
    isNullish(currValue.value) ||
    currValue.value === ''
  ) {
    return false
  }

  /**
   * only if `props.multple` is false
   * and if both open and model value is Array
   */

  if (
    Array.isArray(option) &&
    Array.isArray(currValue.value) &&
    _size(option) && _size(currValue.value) &&
    JSON.stringify(option).toLowerCase() === JSON.stringify(currValue.value).toLowerCase()
  ) {
    return true
  }
  
  return currValue.value.findIndex(item => {
    if (isNullish(item) || item === '') {
      return false
    }

    const data = {
      itemDisplay: item?.[props.dataInputDisplay] ?? item,
      optionDisplay: option?.[props.dataInputDisplay] ?? option,

      itemValue: item?.[props.dataValue] ?? item,
      optionValue: option?.[props.dataValue] ?? option,

      defaultValue: props.defaultValue?.[props.dataValue] ?? props.defaultValue
    }

    const itemDisplay = JSON.stringify(data.itemDisplay).toLowerCase()
    const optionDisplay = JSON.stringify(data.optionDisplay).toLowerCase()

    const itemValue = JSON.stringify(data?.itemValue ?? data.defaultValue).toLowerCase()
    const optionValue = JSON.stringify(data.optionValue).toLowerCase()

    return (itemDisplay === optionDisplay) ||
      (itemValue === optionValue)
  }) !== -1
}

/**
 * This is to check wether dropdownlist is inside viewport,
 * calculates the position the dropdownlist and updates the
 * top and left positions.
 */
function calculatePosition () {
  if (typeof document === 'undefined' || typeof window === 'undefined') {
    return
  }

  const _margin = 10
  const _navHeight = document.querySelector('#main-nav')
    ? document.querySelector('#main-nav')?.clientHeight
    : 56

  const _parent = getElOffset(mainRef.value)
  const _input = getElOffset(inputRef.value?.$el)
  const _list = getElOffset(listRef.value)

  const _inputRect = inputRef.value?.$el?.getBoundingClientRect()

  const _windowHeight = window.innerHeight || document.documentElement.clientHeight

  const viewport = (() => {
    return {
      top: (_input.top - _list.height - _margin - _navHeight) >= 0,
      bottom: (_inputRect?.bottom + _list.height + _margin) <= _windowHeight
    }
  })()

  /**
   * checks is element is still inside the
   * viewport and if not repositions the element
   */
  const newPos = {
    top: _parent.top + _parent.height + _margin,
    left: _parent.left,
    width: _parent.width
  }

  if (viewport.bottom || (!viewport.bottom && !viewport.top)) {
    newPos.top = _parent.top + _parent.height + _margin
  } else {
    // moves the dropdownlist at the top if it doesn't fit the viewport
    newPos.top = _parent.top - _margin - _list.height - 10
  }

  listRect.top = `${newPos?.top}px`
  listRect.left = `${newPos?.left}px`
  listRect.width = `${newPos?.width}px`
}

async function handleFocus () {
  if (inputRef.value?.$el) {
    inputRef.value.$el.focus()

    /**
     * add handling to prevent
     * errors in vitest
     * 
     * click event to trigger AtomsInput active state
     */
    if (!flags.isHandlingClick) {
      flags.isHandlingClick = true
      inputRef.value.$el.click()

      await nextTick()
      flags.isHandlingClick = false
    }
  }

  flags.focus = true
  flags.enterCount = 0

  handleShowDropdown()

  await wait(1)
  await handleFilterListDebounced()
}

function handleBlur () {
  flags.focus = false
  flags.isTyping = false

  flags.mainFocus = -1
  flags.subFocus = -1

  toggle.value = false
  inputRef.value?.$el.dispatchEvent(new Event('blur'))
}

const offsetIndex = ref(0)
function handleActions ($event, action) {
  if (['down', 'up', 'enter'].includes(action)) {
    $event.preventDefault()
  }

  const mainListSize = _size(filteredList.value) - 1
  const currSubListSize = _isEmpty(optionsSizeByGroup?.value)
    ? optionsSizeByGroup?.value
    : (
      optionsSizeByGroup?.value?.[flags.mainFocus] ??
      optionsSizeByGroup?.value?.[0]
    ) - 1

  const prevSubListSize = _isEmpty(optionsSizeByGroup?.value)
    ? optionsSizeByGroup?.value
    : (
      optionsSizeByGroup?.value?.[flags.mainFocus - 1] ??
      optionsSizeByGroup?.value?.[0]
    ) - 1

  const lastMainItem = flags.mainFocus === mainListSize

  function goToElement (main, sub) {
    const mainEl = dropdownListItemsRef.value?.[main]
    const subEl = dropdownSubItemsRef.value?.[sub]

    const subListEl = mainEl.querySelectorAll('ul > li')?.[sub]

    const dpRect = dropdownListRef.value?.getBoundingClientRect()
    const mainElRect = mainEl?.getBoundingClientRect()
    const subEllRect = subListEl?.getBoundingClientRect()
    
    const mainOffset = (mainElRect.top - dpRect.top) + 10

    const offset = mainOffset + (
      subListEl ?
        (subEllRect.top + mainElRect.top)
        : 0
    )

    if (offset >= dpRect.height) {
      offsetIndex.value++
    } else {
      offsetIndex.value--
    }

    if (mainEl) {
      mainEl.scrollIntoView({
        top: dpRect.height * offsetIndex.value,
        behavior: 'smooth',
        block: 'nearest'
      })
    }

    if (subEl) {
      subListEl.scrollIntoView({
        top: dpRect.height * offsetIndex.value,
        behavior: 'smooth',
        block: 'nearest'
      })
    }
  }

  const funcMap = getKey(action, {
    down: () => {
      if (inputStatus.value) { 
        handleShowDropdown()
      }
      
      if (!toggle.value) {
        return
      }

      if (flags.mainFocus <= mainListSize) {
        // data arr not by group
        if (
          !currSubListSize ||
          inputStatus.value === 'typing'
        ) { 
          flags.mainFocus = lastMainItem
            ? mainListSize
            : flags.mainFocus + 1

          goToElement(flags.mainFocus, flags.subFocus)
          return
        }

        // first arrow down click
        if (
          flags.mainFocus === -1 &&
          flags.subFocus === -1
        ) { 
          flags.mainFocus = flags.mainFocus + 1
        }

        if (flags.subFocus < currSubListSize) { 
          flags.subFocus = flags.subFocus + 1
        } else {
          flags.mainFocus = lastMainItem
            ? mainListSize
            : flags.mainFocus + 1

          if (!lastMainItem) {
            flags.subFocus = 0
          }
        }

        goToElement(flags.mainFocus, flags.subFocus)
      }
    },

    up: () => {
      if (flags.mainFocus >= 0) { 
        // data arr not by group
        if (
          !currSubListSize ||
          inputStatus.value === 'typing'
        ) { 
          flags.mainFocus = flags.mainFocus <= 0
            ? 0
            : flags.mainFocus - 1

          goToElement(flags.mainFocus, flags.subFocus)
          return
        }

        if (flags.subFocus === 0 && flags.mainFocus === 0) {
          flags.subFocus = 0

          goToElement(flags.mainFocus, flags.subFocus)
          return
        }

        if (flags.subFocus > 0 && flags.subFocus <= prevSubListSize) { 
          flags.subFocus = flags.subFocus - 1
        } else {
          flags.mainFocus = flags.mainFocus > 0
            ? flags.mainFocus - 1
            : 0
          flags.subFocus = prevSubListSize
        }

        goToElement(flags.mainFocus, flags.subFocus)
      }
    },

    backspace: async () => { 
      await wait(1)

      if (inputText.value) {
        return
      }
      
      selected.values.pop()
      selected.input.pop()

      emit('update:modelValue', selected.values)
    },

    enter: () => {
      const item = (() => { 
        if (
          !currSubListSize || inputStatus.value === 'typing'
        ) { 
          return filteredList.value[flags.mainFocus]
        }
        
        return filteredList.value?.[flags.mainFocus]?.options?.[flags.subFocus]
      })()

      if (item) {
        handleSelectItem(item)
      }
    },

    tab: () => { 
      handleBlur()
    }
  })

  if (typeof funcMap === 'function') {
    funcMap?.()
  }
}

function removeSelection (item) {
  if (!props.multiple) {
    return
  }

  const index = currValue.value.findIndex(option => (option?.[props.dataInputDisplay] || option) === (item?.[props.dataInputDisplay] || item))

  if (index > -1) { 
    currValue.value.splice(index, 1)
  }

  emit('update:modelValue', currValue.value)
}

function resetSelection () {
  selected.values = props.multiple ? initialSelection.value.values : initialSelection.value.values?.[0]
  selected.input = props.multiple ? initialSelection.value.input : initialSelection.value.input?.[0]

  emit('update:modelValue', selected.values)
}

defineExpose({
  selected: _selected,
  resetSelection
})
</script>
